import { useLingui } from "@lingui/react";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import { Tab, TabPanel, Tabs } from "@warp-ds/elements/react/tabs";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import type { SafetyElementsProps } from "./Index.tsx";
import { useSafetyElements } from "./useSafetyElements.tsx";

const tabId = (id: string) => `safety-element-tab-${id}`;
const panelId = (id: string) => `safety-element-panel-${id}`;

const SafetyElementsDesktop = ({ safetyElements, memberAssociations }: SafetyElementsProps) => {
  const { i18n } = useLingui();

  const elements = useSafetyElements({ safetyElements, memberAssociations });
  const { trackClick } = usePulse();
  const safetyElementsAria = i18n._({
    id: "itemPageLayout.safetyElement.ariaLabel",
    message: "Safety elements",
  });

  const onClickTrackEvent = (trackingName: string, trackingId: string) => {
    // Tracking event: MO_ev42, MO_ev43, MO_ev44, MO_ev45, MO_ev46, MO_ev64, MO_ev62
    trackClick({
      name: trackingName,
      objectId: trackingId,
      elementType: ElementType.Tab,
      intent: Intent.Open,
    });
  };

  if (elements.length === 0) return null;
  return (
    <div className="mt-40 pt-40 border-t">
      <Tabs
        aria-label={safetyElementsAria}
        active={panelId(elements[0].id)}
        // use flex instead of the default inline-grid so 5+ tabs don't get squeezed into equal-width columns
        className="w-full part-[tablist]:flex"
      >
        {elements.map((element, index) => (
          <Tab
            // the new icons have some space around them so we need to reduce the gap between icon and label
            className="part-[base]:gap-0 part-[base]:px-12"
            key={element.id}
            id={tabId(element.id)}
            htmlFor={panelId(element.id)}
            tabIndex={index === 0 ? 0 : -1}
            onClick={() => onClickTrackEvent(element.trackingName, element.trackingId)}
            over
          >
            <span slot="icon">{element.icon}</span>
            {element.label}
          </Tab>
        ))}
        {elements.map((element) => (
          <TabPanel key={element.id} id={panelId(element.id)}>
            {element.content}
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
};

export default SafetyElementsDesktop;
